/**
 * MapContext - Provides map controls via React Context instead of global window object
 */

import React, { createContext, useContext, ReactNode } from 'react'

/**
 * Geographic coordinate representation using latitude and longitude.
 * 
 * @interface LatLng
 * @example
 * ```typescript
 * const newYork: LatLng = { lat: 40.7128, lng: -74.0060 }
 * const london: LatLng = { lat: 51.5074, lng: -0.1278 }
 * ```
 */
export interface LatLng {
  /** Latitude in decimal degrees (-90 to 90) */
  lat: number
  /** Longitude in decimal degrees (-180 to 180) */
  lng: number
}

/**
 * Complete viewport state representing the current view of the map.
 * Contains all information needed to describe the map's current position and settings.
 * 
 * @interface MapViewport
 * @example
 * ```typescript
 * const viewport: MapViewport = {
 *   center: { lat: 40.7128, lng: -74.0060 },
 *   zoom: 12,
 *   bounds: map.getBounds(),
 *   heading: 45,  // Optional: for 3D maps
 *   tilt: 30      // Optional: for 3D maps
 * }
 * ```
 */
export interface MapViewport {
  /** The center point of the map view */
  center: LatLng
  /** Zoom level (0-25, where higher numbers show more detail) */
  zoom: number
  /** Current visible bounds of the map (automatically calculated) */
  bounds?: google.maps.LatLngBounds
  /** Compass heading in degrees (0-360) for 3D maps. 0 = North, 90 = East */
  heading?: number
  /** Tilt angle in degrees (0-45) for 3D maps. 0 = top-down view */
  tilt?: number
  /** Map display type (roadmap, satellite, hybrid, terrain) */
  mapTypeId?: google.maps.MapTypeId
}

/**
 * Partial viewport update interface for programmatic map control.
 * All fields are optional, allowing for granular updates to specific viewport properties.
 * 
 * ## Usage Guidelines:
 * 
 * ### Partial Updates (Recommended)
 * Update only the properties you want to change:
 * ```typescript
 * // Just change zoom level
 * updateMapViewport({ zoom: 15 })
 * 
 * // Just change center
 * updateMapViewport({ center: { lat: 40.7128, lng: -74.0060 } })
 * 
 * // Change only latitude, keep longitude unchanged
 * updateMapViewport({ center: { lat: 41.0 } })
 * ```
 * 
 * ### Full Updates
 * Provide complete viewport state when doing major changes:
 * ```typescript
 * updateMapViewport({
 *   center: { lat: 40.7128, lng: -74.0060 },
 *   zoom: 12,
 *   heading: 0,
 *   tilt: 0
 * })
 * ```
 * 
 * ### Animation Control
 * Use the `animate` parameter to control smooth transitions:
 * ```typescript
 * // Animated transition (default)
 * updateMapViewport({ zoom: 15 }, true)
 * 
 * // Instant update
 * updateMapViewport({ zoom: 15 }, false)
 * ```
 * 
 * @interface MapViewportUpdate
 */
export interface MapViewportUpdate {
  /** 
   * Center coordinates to update. Can be partial (lat only, lng only, or both).
   * When partial, only specified coordinates are updated.
   */
  center?: Partial<LatLng>
  /** New zoom level (0-25). Higher values show more detail. */
  zoom?: number
  /** 
   * Bounds to fit the map to. When specified, center and zoom are calculated automatically.
   * Takes precedence over individual center/zoom settings.
   */
  bounds?: google.maps.LatLngBounds
  /** 
   * Compass heading in degrees (0-360) for 3D maps. 
   * 0 = North, 90 = East, 180 = South, 270 = West 
   */
  heading?: number
  /** 
   * Tilt angle in degrees (0-45) for 3D maps. 
   * 0 = top-down view, 45 = maximum tilt 
   */
  tilt?: number
  /** Map display type to switch to */
  mapTypeId?: google.maps.MapTypeId
}

/**
 * Typed error union representing different categories of map-related errors.
 * Provides structured error information for proper error handling and user feedback.
 * 
 * @example
 * ```typescript
 * const handleMapError = (error: MapError) => {
 *   switch (error.type) {
 *     case 'API_LOAD_FAILED':
 *       showToast('Failed to load map. Check your internet connection.')
 *       break
 *     case 'INVALID_COORDINATES':
 *       console.error(`Invalid coordinates: ${error.coordinates.lat}, ${error.coordinates.lng}`)
 *       break
 *     case 'PERMISSION_DENIED':
 *       showLocationPermissionDialog()
 *       break
 *     default:
 *       console.error('Map error:', error.message)
 *   }
 * }
 * ```
 */
export type MapError = 
  /** Google Maps API failed to load or initialize */
  | { type: 'API_LOAD_FAILED'; message: string }
  /** Provided coordinates are invalid (out of range or malformed) */
  | { type: 'INVALID_COORDINATES'; message: string; coordinates: { lat: number; lng: number } }
  /** User denied location permissions */
  | { type: 'PERMISSION_DENIED'; message: string }
  /** Error occurred during viewport operations */
  | { type: 'VIEWPORT_ERROR'; message: string }
  /** Error occurred during pan operations */
  | { type: 'PAN_ERROR'; message: string }
  /** Catch-all for unexpected errors */
  | { type: 'UNKNOWN_ERROR'; message: string; originalError?: Error }

/**
 * Comprehensive map controller interface providing programmatic control over map behavior.
 * This interface abstracts Google Maps API calls and provides a consistent, type-safe way
 * to interact with the map from React components.
 * 
 * ## Usage Examples:
 * 
 * ### Basic Navigation
 * ```typescript
 * const mapControls = useMapControls()
 * 
 * // Pan to a specific location
 * mapControls.panTo({ lat: 40.7128, lng: -74.0060 })
 * 
 * // Zoom to a specific level
 * mapControls.setZoom(15)
 * 
 * // Get current state
 * const viewport = mapControls.getCurrentViewport()
 * ```
 * 
 * ### Advanced Operations
 * ```typescript
 * // Fit multiple locations in view
 * const bounds = new google.maps.LatLngBounds()
 * locations.forEach(loc => bounds.extend(loc))
 * mapControls.fitBounds(bounds)
 * 
 * // Update multiple properties atomically
 * mapControls.updateMapViewport({
 *   center: { lat: 40.7128, lng: -74.0060 },
 *   zoom: 12,
 *   heading: 45
 * })
 * ```
 * 
 * @interface MapController
 */
export interface MapController {
  /**
   * Get the current complete viewport state of the map.
   * Returns null if the map is not yet initialized or ready.
   * 
   * @returns Current viewport including center, zoom, bounds, and 3D properties, or null if not available
   * 
   * @example
   * ```typescript
   * const viewport = mapControls.getCurrentViewport()
   * if (viewport) {
   *   console.log(`Map centered at ${viewport.center.lat}, ${viewport.center.lng}`)
   *   console.log(`Zoom level: ${viewport.zoom}`)
   * }
   * ```
   */
  getCurrentViewport: () => MapViewport | null

  /**
   * Update the map viewport with new settings. Supports partial updates.
   * All viewport properties are optional - only specified properties will be changed.
   * 
   * @param viewport - Partial viewport update containing properties to change
   * @param animate - Whether to animate the transition (default: true)
   * 
   * @example
   * ```typescript
   * // Animate to new location and zoom
   * mapControls.updateMapViewport({
   *   center: { lat: 40.7128, lng: -74.0060 },
   *   zoom: 15
   * }, true)
   * 
   * // Instantly update only zoom level
   * mapControls.updateMapViewport({ zoom: 12 }, false)
   * 
   * // Update only latitude, keeping longitude unchanged
   * mapControls.updateMapViewport({
   *   center: { lat: 41.0 }
   * })
   * ```
   */
  updateMapViewport: (viewport: MapViewportUpdate, animate?: boolean) => void
  
  /**
   * Pan the map to center on the specified coordinates.
   * More direct than updateMapViewport when only changing center position.
   * 
   * @param center - Target coordinates to center the map on
   * @param animate - Whether to animate the pan (default: true)
   * 
   * @example
   * ```typescript
   * // Smoothly pan to New York City
   * mapControls.panTo({ lat: 40.7128, lng: -74.0060 })
   * 
   * // Instantly jump to London
   * mapControls.panTo({ lat: 51.5074, lng: -0.1278 }, false)
   * ```
   */
  panTo: (center: LatLng, animate?: boolean) => void

  /**
   * Pan the map by the specified pixel offsets relative to the current center.
   * Useful for fine-grained adjustments or responding to user interactions.
   * 
   * @param deltaX - Horizontal offset in pixels (positive = east, negative = west)
   * @param deltaY - Vertical offset in pixels (positive = south, negative = north)
   * 
   * @example
   * ```typescript
   * // Pan 100 pixels east and 50 pixels north
   * mapControls.panBy(100, -50)
   * 
   * // Compensate for panel expansion (pan left)
   * mapControls.panBy(-300, 0)
   * ```
   */
  panBy: (deltaX: number, deltaY: number) => void

  /**
   * Set the map zoom level.
   * More direct than updateMapViewport when only changing zoom.
   * 
   * @param zoom - Target zoom level (0-25, where higher numbers show more detail)
   * @param animate - Whether to animate the zoom transition (default: true)
   * 
   * @example
   * ```typescript
   * // Zoom in to street level
   * mapControls.setZoom(16)
   * 
   * // Instantly zoom out to city level
   * mapControls.setZoom(10, false)
   * ```
   */
  setZoom: (zoom: number, animate?: boolean) => void

  /**
   * Adjust the map viewport to show all locations within the specified bounds.
   * Automatically calculates appropriate center and zoom level.
   * 
   * @param bounds - Geographic bounds to fit within the viewport
   * @param animate - Whether to animate the transition (default: true)
   * 
   * @example
   * ```typescript
   * // Fit multiple locations in view
   * const bounds = new google.maps.LatLngBounds()
   * bounds.extend({ lat: 40.7128, lng: -74.0060 }) // NYC
   * bounds.extend({ lat: 34.0522, lng: -118.2437 }) // LA
   * mapControls.fitBounds(bounds)
   * 
   * // Show search results area
   * mapControls.fitBounds(searchResultsBounds, true)
   * ```
   */
  fitBounds: (bounds: google.maps.LatLngBounds, animate?: boolean) => void
  
  /**
   * Get direct access to the underlying Google Maps instance.
   * Use with caution - direct manipulation bypasses the controller's state management.
   * Returns null if the map is not yet initialized.
   * 
   * @returns The Google Maps instance or null if not available
   * 
   * @example
   * ```typescript
   * const map = mapControls.getMapInstance()
   * if (map) {
   *   // Use Google Maps API directly
   *   map.setOptions({ styles: customMapStyles })
   *   
   *   // Add event listeners
   *   map.addListener('click', handleMapClick)
   * }
   * ```
   */
  getMapInstance: () => google.maps.Map | null
  
  /**
   * Check if the map is fully initialized and ready for operations.
   * 
   * @returns true if the map is ready, false otherwise
   * 
   * @example
   * ```typescript
   * if (mapControls.isReady()) {
   *   // Safe to perform map operations
   *   mapControls.panTo(newLocation)
   * } else {
   *   // Wait for map to initialize
   *   console.log('Map not ready yet')
   * }
   * ```
   */
  isReady: () => boolean

  /**
   * Get the current center coordinates of the map.
   * More lightweight than getCurrentViewport() when only center is needed.
   * 
   * @returns Current center coordinates or null if map not ready
   * 
   * @example
   * ```typescript
   * const center = mapControls.getCenter()
   * if (center) {
   *   console.log(`Currently viewing ${center.lat}, ${center.lng}`)
   * }
   * ```
   */
  getCenter: () => LatLng | null

  /**
   * Get the current zoom level of the map.
   * More lightweight than getCurrentViewport() when only zoom is needed.
   * 
   * @returns Current zoom level or null if map not ready
   * 
   * @example
   * ```typescript
   * const zoom = mapControls.getZoom()
   * if (zoom !== null) {
   *   console.log(`Current zoom: ${zoom}`)
   *   
   *   // Conditional logic based on zoom
   *   if (zoom > 15) {
   *     showDetailedMarkers()
   *   }
   * }
   * ```
   */
  getZoom: () => number | null

  /**
   * Get the current visible bounds of the map.
   * Useful for determining which markers or data to display.
   * 
   * @returns Current visible bounds or null if map not ready
   * 
   * @example
   * ```typescript
   * const bounds = mapControls.getBounds()
   * if (bounds) {
   *   // Filter markers to only show those in view
   *   const visibleMarkers = allMarkers.filter(marker =>
   *     bounds.contains(marker.position)
   *   )
   * }
   * ```
   */
  getBounds: () => google.maps.LatLngBounds | null
}

/**
 * Map state interface for tracking the overall status and health of the map component.
 * Used by providers and components to understand the current state of map initialization.
 * 
 * @interface MapState
 * @example
 * ```typescript
 * const mapState: MapState = {
 *   isLoaded: true,
 *   isReady: true,
 *   hasError: false
 * }
 * 
 * // Conditional rendering based on state
 * if (!mapState.isLoaded) {
 *   return <MapSkeleton />
 * } else if (mapState.hasError) {
 *   return <MapErrorFallback error={mapState.lastError} />
 * } else if (!mapState.isReady) {
 *   return <MapLoadingSpinner />
 * }
 * ```
 */
export interface MapState {
  /** Whether the Google Maps API has been loaded */
  isLoaded: boolean
  /** Whether the map is fully initialized and ready for operations */
  isReady: boolean
  /** Whether the map has encountered any errors */
  hasError: boolean
  /** The most recent error, if any */
  lastError?: MapError
}

const MapContext = createContext<MapController | null>(null)

/**
 * React Context Provider for sharing map controller across the component tree.
 * Provides the MapController instance to child components via React Context.
 * 
 * @param props - Provider props
 * @param props.children - Child components that will have access to the map controller
 * @param props.controller - MapController instance to provide, or null if not ready
 * 
 * @example
 * ```typescript
 * function App() {
 *   const [controller, setController] = useState<MapController | null>(null)
 * 
 *   return (
 *     <MapProvider controller={controller}>
 *       <Map onControllerReady={setController} />
 *       <MapNavigationPanel />
 *       <PlaceSearchPanel />
 *     </MapProvider>
 *   )
 * }
 * ```
 */
export function MapProvider({ 
  children, 
  controller 
}: { 
  children: ReactNode
  controller: MapController | null
}) {
  return (
    <MapContext.Provider value={controller}>
      {children}
    </MapContext.Provider>
  )
}

/**
 * Hook to access the map controller from any component within a MapProvider.
 * Throws an error if used outside of a MapProvider context.
 * 
 * @returns The MapController instance
 * @throws Error if used outside MapProvider
 * 
 * @example
 * ```typescript
 * function MapNavigationPanel() {
 *   const mapControls = useMapControls()
 * 
 *   const handleZoomIn = () => {
 *     const currentZoom = mapControls.getZoom()
 *     if (currentZoom !== null) {
 *       mapControls.setZoom(currentZoom + 1)
 *     }
 *   }
 * 
 *   return (
 *     <button onClick={handleZoomIn}>
 *       Zoom In
 *     </button>
 *   )
 * }
 * ```
 */
export function useMapControls(): MapController {
  const context = useContext(MapContext)
  if (!context) {
    throw new Error('useMapControls must be used within a MapProvider')
  }
  return context
}

/**
 * Hook to optionally access the map controller, returning null if not available.
 * Useful for components that can work with or without map integration.
 * 
 * @returns The MapController instance or null if not available
 * 
 * @example
 * ```typescript
 * function OptionalMapIntegration() {
 *   const mapControls = useMapControlsOptional()
 * 
 *   const handleLocationClick = (location: LatLng) => {
 *     if (mapControls) {
 *       // Map is available - pan to location
 *       mapControls.panTo(location)
 *     } else {
 *       // Map not available - handle differently
 *       window.open(`https://maps.google.com/?q=${location.lat},${location.lng}`)
 *     }
 *   }
 * 
 *   return <LocationButton onClick={handleLocationClick} />
 * }
 * ```
 */
export function useMapControlsOptional(): MapController | null {
  return useContext(MapContext)
}